import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import './bookingsList.css';
import { statusBadgeClass } from '../../../../utils/statusBadge';

/**
 * The property's booking history — one card, compact rows, and an empty state
 * that says so quietly instead of rendering a bare toggle over nothing
 * (owner, 2026-09-02).
 *
 * The same card closes the CLIENT page (owner, 2026-09-18): pass `clientId`
 * instead of `propertyId` and it lists every booking of the client across
 * all their properties — each row then leads with the unit it is for.
 */
function BookingsList({ propertyId, clientId }) {
    const forClient = clientId != null;
    const source = forClient ? `/api/clients/${clientId}/bookings` : `/api/properties/bookings/${propertyId}`;
    const [bookings, setBookings] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [view, setView] = useState('future');
    const navigate = useNavigate();

    const fetchBookings = useCallback(async (page, viewType) => {
        try {
            const response = await axios.get(`${source}/${viewType}?page=${page}`);
            setBookings(response.data.bookings);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error(`Error fetching ${viewType} bookings:`, error);
            setBookings([]);
        } finally {
            setLoaded(true);
        }
    }, [source]);

    useEffect(() => {
        fetchBookings(currentPage, view);
    }, [fetchBookings, currentPage, view]);



    return (
        <div className="dp-card pd-bookings">
            <div className="bk-head dp-cardHead">
                <h2>Bookings</h2>
                <div className="bk-toggle">
                    <button
                        className={`bk-toggleBtn ${view === 'future' ? 'is-active' : ''}`}
                        onClick={() => { setView('future'); setCurrentPage(1); }}
                    >
                        Upcoming
                    </button>
                    <button
                        className={`bk-toggleBtn ${view === 'past' ? 'is-active' : ''}`}
                        onClick={() => { setView('past'); setCurrentPage(1); }}
                    >
                        Past
                    </button>
                </div>
            </div>

            {loaded && bookings.length === 0 ? (
                <div className="bk-empty">
                    {view === 'future' ? 'No upcoming bookings scheduled.' : `No past bookings for this ${forClient ? 'client' : 'property'}.`}
                </div>
            ) : (
                <div className="bk-list">
                    {bookings.map((booking) => {
                        const d = DateTime.fromISO(booking.booking_start, { zone: 'Asia/Dubai' });
                        return (
                            <div
                                className="bk-row"
                                key={booking.booking_id}
                                onClick={() => navigate(`/bookings/${booking.booking_id}`)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => (e.key === 'Enter') && navigate(`/bookings/${booking.booking_id}`)}
                            >
                                <div className="bk-date">
                                    <span className="bk-date-day">{d.toFormat('d')}</span>
                                    <span className="bk-date-rest">{d.toFormat('LLL')}{d.year !== DateTime.now().year ? ` ${d.toFormat('yy')}` : ''}</span>
                                </div>
                                <div className="bk-main">
                                    <div className="bk-line1">
                                        {forClient && (
                                            <span className="bk-type">
                                                {[booking.unit_number, booking.building_name].filter(Boolean).join(' - ') || `Property #${booking.property_id}`}
                                            </span>
                                        )}
                                        <span className={forClient ? 'bk-subtype' : 'bk-type'}>{booking.booking_type}</span>
                                        <span className={statusBadgeClass(booking.booking_status)}>
                                            {booking.booking_status}
                                        </span>
                                    </div>
                                    <div className="bk-line2">
                                        {d.toFormat('cccc')} · {d.toFormat('HH:mm')} · {booking.duration_hours} hrs
                                        {booking.assigned_cleaners ? ` · ${booking.assigned_cleaners}` : ' · no cleaners assigned'}
                                    </div>
                                </div>
                                <div className="bk-right">
                                    <span className="bk-id">#{booking.booking_id}</span>
                                    <span className="bk-chevron">›</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className="bk-pagination">
                    <button
                        className="bk-pageBtn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((prev) => prev - 1)}
                    >
                        Previous
                    </button>
                    <span className="bk-pageInfo">Page {currentPage} of {totalPages}</span>
                    <button
                        className="bk-pageBtn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default BookingsList;
